import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Calendar, Users, AlertTriangle, TrendingUp, Filter, Download, Printer, Activity, FileText, Heart, RotateCcw } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { ThirtyDayOutcomesReport, ReportFilters } from '../../../types/report.types';
import { toast } from '../../../hooks/use-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { adminService } from '../../../services/adminService';
import { UserLevel } from '../../../types/auth.types';
import { reportService } from '../../../services/reportService';
import { api } from '../../../services/api';


const COLORS = ['#0088FE', '#00C499', '#FFBB28', '#FF8042', '#8884d8'];

const OutcomesAnalysisReport: React.FC = () => {
    const { utilizator } = useAuth();
    const isAdmin = utilizator?.user_level_id === UserLevel.ADMINISTRATOR;

    const [data, setData] = useState<ThirtyDayOutcomesReport | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [filters, setFilters] = useState<ReportFilters>({
        start_date: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        hospital_id: isAdmin ? undefined : (utilizator?.hospital_id || undefined)
    });

    const [hospitals, setHospitals] = useState<{ id: number; name: string }[]>([]);

    useEffect(() => {
        const loadHospitals = async () => {
            if (isAdmin) {
                try {
                    const hospitalList = await adminService.listHospitals();
                    setHospitals(hospitalList);
                } catch (error) {
                    console.error('Failed to load hospitals:', error);
                    toast({
                        title: "Eroare",
                        description: "Nu s-a putut încărca lista de spitale.",
                        variant: "destructive",
                    });
                }
            } else if (utilizator?.hospital_id) {
                // For non-admins, we can just set their current hospital as the only option if needed,
                // or just rely on the disabled state.
                // But to be safe/consistent with the Select, we might want to have it in the list.
                // However, the Select value will be set to their ID, so it should display correctly 
                // if we don't populate the list, it might show the ID or empty.
                // Better to not populate for non-admins and just show the ID or a static "My Hospital" label if we had it.
                // Actually, the previous implementation had hardcoded hospitals. 
                // Let's try to fetch the hospital name if possible, or just leave it as is since the filter is disabled.
            }
        };
        loadHospitals();
    }, [isAdmin, utilizator]);

    const fetchData = async () => {
        if (!filters.start_date || !filters.end_date) {
            toast({
                title: "Eroare",
                description: "Vă rugăm să selectați perioada de raportare.",
                variant: "destructive",
            });
            return;
        }

        setLoading(true);
        try {
            const params: any = {};
            if (filters.start_date) params.start_date = filters.start_date;
            if (filters.end_date) params.end_date = filters.end_date;
            if (filters.hospital_id) params.hospital_id = filters.hospital_id;

            const response = await api.get<ThirtyDayOutcomesReport>('/api/reports/30-day-outcomes', { params });

            console.log('Backend response:', response);

            if (response.success) {
                setData(response.data!);
                toast({
                    title: "Success",
                    description: "Raportul a fost generat cu succes.",
                });
            } else {
                console.error('Response not successful:', response);
                throw new Error(response.error || 'Unknown error');
            }
        } catch (error) {
            console.error('Error fetching 30-day outcomes report:', error);
            toast({
                title: "Eroare",
                description: "A apărut o eroare la generarea raportului.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Initial fetch only if we have necessary data
        if (filters.hospital_id || isAdmin) {
            fetchData();
        }
    }, []);

    const exportToPDF = async () => {
        if (!data) return;

        try {
            const blob = await reportService.exportToPdf({
                report_data: data,
                report_type: '30_day_outcomes',
                title: 'Rezultate la 30 de Zile'
            });

            reportService.downloadFile(blob, `30_day_outcomes_${new Date().toISOString().split('T')[0]}.pdf`);

            toast({
                title: "Success",
                description: "Raportul a fost exportat în PDF.",
            });
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast({
                title: "Eroare",
                description: "Nu s-a putut exporta raportul în PDF",
                variant: "destructive"
            });
        }
    };

    const exportToExcel = async () => {
        if (!data) return;

        try {
            const blob = await reportService.exportToExcel({
                report_data: data,
                report_type: '30_day_outcomes',
                title: 'Rezultate la 30 de Zile'
            });

            reportService.downloadFile(blob, `30_day_outcomes_${new Date().toISOString().split('T')[0]}.xlsx`);

            toast({
                title: "Success",
                description: "Raportul a fost exportat în Excel.",
            });
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast({
                title: "Eroare",
                description: "Nu s-a putut exporta raportul în Excel",
                variant: "destructive"
            });
        }
    };

    const prepareChartData = () => {
        if (!data || !data.outcomes_by_diagnosis || data.outcomes_by_diagnosis.length === 0) {
            return [];
        }

        const diagnosisData = data.outcomes_by_diagnosis.map((item) => ({
            name: `${item.icd10_code} `,
            description: item.description.substring(0, 20) + '...',
            total: item.total_patients,
            mortality: item.mortality,
            mortalityRate: item.total_patients > 0 ? Math.round((item.mortality / item.total_patients) * 100) : 0,
            complications: item.complications,
            reoperations: item.reoperations
        }));

        return diagnosisData;
    };

    const preparePieData = () => {
        if (!data) return [];

        return [
            { name: 'Fără complicații', value: data.summary.total_patients - Math.round((data.summary.complication_rate / 100) * data.summary.total_patients) },
            { name: 'Cu complicații', value: Math.round((data.summary.complication_rate / 100) * data.summary.total_patients) }
        ];
    };

    return (
        <div className="space-y-6 print:space-y-4">
            <style>{`
@media print {
                    .no - print { display: none!important; }
                    .print - only { display: block!important; }
                    body { background: white; }
                    .card { box - shadow: none; border: 1px solid #ddd; }
}
`}</style>

            <div className="flex justify-between items-center no-print">
                <h2 className="text-3xl font-bold tracking-tight">Analiza rezultate la 30 de zile</h2>
                <div className="flex space-x-2">
                    <Button onClick={exportToExcel} variant="outline" size="sm">
                        <Download className="mr-2 h-4 w-4" />
                        Export Excel
                    </Button>
                    <Button onClick={exportToPDF} variant="outline" size="sm">
                        <Printer className="mr-2 h-4 w-4" />
                        Tipărește PDF
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card className="no-print">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Filter className="h-5 w-5" />
                        Filtre raport
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="start-date">Data început</Label>
                            <input
                                id="start-date"
                                type="date"
                                value={filters.start_date}
                                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="end-date">Data sfârșit</Label>
                            <input
                                id="end-date"
                                type="date"
                                value={filters.end_date}
                                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="hospital">Spital</Label>
                            <Select
                                value={filters.hospital_id?.toString() || 'all'}
                                onValueChange={(value) => setFilters(prev => ({
                                    ...prev,
                                    hospital_id: value === 'all' ? undefined : parseInt(value)
                                }))}
                                disabled={!isAdmin}
                            >
                                <SelectTrigger id="hospital">
                                    <SelectValue placeholder={isAdmin ? "Toate spitalele" : (utilizator?.hospital_name || "Spitalul curent")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {isAdmin && <SelectItem value="all">Toate spitalele</SelectItem>}
                                    {isAdmin ? (
                                        hospitals.map(h => (
                                            <SelectItem key={h.id} value={h.id.toString()}>
                                                {h.name}
                                            </SelectItem>
                                        ))
                                    ) : (
                                        utilizator?.hospital_id && (
                                            <SelectItem value={utilizator.hospital_id.toString()}>
                                                {utilizator.hospital_name || "Spitalul curent"}
                                            </SelectItem>
                                        )
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end">
                            <Button onClick={fetchData} disabled={loading} className="w-full">
                                <Activity className="mr-2 h-4 w-4" />
                                {loading ? 'Se încarcă...' : 'Generează raport'}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {loading && (
                <div className="flex justify-center py-8">
                    <LoadingSpinner />
                </div>
            )}

            {!loading && data && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-center">
                                    <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                                    <p className="text-3xl font-bold text-blue-600">{data.summary.total_patients}</p>
                                    <p className="text-sm text-muted-foreground">Total pacienți</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-center">
                                    <Activity className="h-8 w-8 mx-auto mb-2 text-red-600" />
                                    <p className="text-3xl font-bold text-red-600">{data.summary.mortality_rate}%</p>
                                    <p className="text-sm text-muted-foreground">Rata mortalitate</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-center">
                                    <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                                    <p className="text-3xl font-bold text-orange-600">{data.summary.reoperation_rate}%</p>
                                    <p className="text-sm text-muted-foreground">Rata reoperare</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="text-center">
                                    <FileText className="h-8 w-8 mx-auto mb-2 text-green-600" />
                                    <p className="text-3xl font-bold text-green-600">{data.summary.readmission_rate}%</p>
                                    <p className="text-sm text-muted-foreground">Rata readmisie</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <Tabs defaultValue="overview" className="w-full">
                        <TabsList className="grid w-full grid-cols-4 no-print">
                            <TabsTrigger value="overview">Sinteză</TabsTrigger>
                            <TabsTrigger value="diagnosis">Pe diagnostic</TabsTrigger>
                            <TabsTrigger value="procedures">Pe proceduri</TabsTrigger>
                            <TabsTrigger value="details">Detalii pacienți</TabsTrigger>
                        </TabsList>

                        <TabsContent value="overview" className="space-y-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Analiză mortalitate</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="h-[300px]">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={[
                                                            { name: 'Precoce (≤30 zile)', value: data.mortality_analysis.early_mortality },
                                                            { name: 'Tardivă (>30 zile)', value: data.mortality_analysis.late_mortality }
                                                        ]}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={80}
                                                        fill="#8884d8"
                                                        paddingAngle={5}
                                                        dataKey="value"
                                                    >
                                                        <Cell fill="#ef4444" />
                                                        <Cell fill="#f97316" />
                                                    </Pie>
                                                    <Tooltip />
                                                    <Legend />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader>
                                        <CardTitle>Rezultate pe diagnostic (Top 5)</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {data.outcomes_by_diagnosis && data.outcomes_by_diagnosis.length > 0 ? (
                                            <div className="h-[300px]">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        data={data.outcomes_by_diagnosis
                                                            .slice(0, 5)
                                                            .map((item) => ({
                                                                name: item.icd10_code,
                                                                mortalitate: item.mortality,
                                                                reoperatii: item.reoperations,
                                                                readmisii: item.readmissions
                                                            }))}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="name" />
                                                        <YAxis />
                                                        <Tooltip />
                                                        <Legend />
                                                        <Bar dataKey="mortalitate" fill="#ef4444" name="Decese" />
                                                        <Bar dataKey="reoperatii" fill="#f97316" name="Reoperații" />
                                                        <Bar dataKey="readmisii" fill="#3b82f6" name="Readmisii" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        ) : (
                                            <div className="text-center py-8 text-muted-foreground h-[300px] flex items-center justify-center">
                                                <div>
                                                    <Heart className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                                    <p>Nu există date despre diagnostice disponibile.</p>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Complicații frecvente</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.complications && data.complications.length > 0 ? (
                                        <div className="space-y-4">
                                            {data.complications.slice(0, 8).map((comp, index) => (
                                                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                                    <div className="flex-1">
                                                        <span className="font-medium">{comp.complications}</span>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {comp.patient_name} ({comp.patient_code}) - {comp.procedure_name}
                                                        </p>
                                                    </div>
                                                    <Badge variant="outline">{comp.procedure_date}</Badge>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                            <p>Nu există date despre complicații disponibile pentru perioada selectată.</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="diagnosis" className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Rezultate pe diagnostic</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.outcomes_by_diagnosis && data.outcomes_by_diagnosis.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full border-collapse">
                                                <thead>
                                                    <tr className="border-b">
                                                        <th className="text-left p-2">Cod ICD-10</th>
                                                        <th className="text-left p-2">Descriere</th>
                                                        <th className="text-center p-2">Total</th>
                                                        <th className="text-center p-2">Mortalitate</th>
                                                        <th className="text-center p-2">Reoperații</th>
                                                        <th className="text-center p-2">Readmisii</th>
                                                        <th className="text-center p-2">Complicații</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.outcomes_by_diagnosis.map((item, index) => (
                                                        <tr key={index} className="border-b hover:bg-gray-50">
                                                            <td className="p-2 font-medium">{item.icd10_code}</td>
                                                            <td className="p-2">{item.description}</td>
                                                            <td className="text-center p-2">{item.total_patients}</td>
                                                            <td className="text-center p-2">
                                                                <span className="text-red-600">{item.mortality}</span>
                                                                <span className="text-xs text-gray-500 ml-1">
                                                                    ({item.total_patients > 0 ? Math.round((item.mortality / item.total_patients) * 100) : 0}%)
                                                                </span>
                                                            </td>
                                                            <td className="text-center p-2 text-orange-600">{item.reoperations}</td>
                                                            <td className="text-center p-2 text-blue-600">{item.readmissions}</td>
                                                            <td className="text-center p-2 text-yellow-600">{item.complications}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <Heart className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                            <p>Nu există date despre diagnostice disponibile pentru perioada selectată.</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Grafic mortalitate pe diagnostic</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ResponsiveContainer width="100%" height={400}>
                                        <BarChart data={prepareChartData()}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="total" fill="#8884d8" name="Total pacienți" />
                                            <Bar dataKey="mortality" fill="#ff7300" name="Decese" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="procedures" className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Rezultate pe proceduri</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {data.outcomes_by_procedure && data.outcomes_by_procedure.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full border-collapse">
                                                <thead>
                                                    <tr className="border-b">
                                                        <th className="text-left p-2">Nume procedură</th>
                                                        <th className="text-center p-2">Total</th>
                                                        <th className="text-center p-2">Succes</th>
                                                        <th className="text-center p-2">Eșec</th>
                                                        <th className="text-center p-2">Reoperații</th>
                                                        <th className="text-center p-2">Rata succes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.outcomes_by_procedure.map((item, index) => (
                                                        <tr key={index} className="border-b hover:bg-gray-50">
                                                            <td className="p-2 font-medium">{item.procedure_name}</td>
                                                            <td className="text-center p-2">{item.total}</td>
                                                            <td className="text-center p-2 text-green-600">{item.successful}</td>
                                                            <td className="text-center p-2 text-red-600">{item.failed}</td>
                                                            <td className="text-center p-2 text-orange-600">{item.reoperations}</td>
                                                            <td className="text-center p-2">
                                                                <Badge variant={item.success_rate >= 90 ? 'default' : 'secondary'}>
                                                                    {item.success_rate.toFixed(1)}%
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                            <RotateCcw className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                            <p>Nu există date despre proceduri disponibile pentru perioada selectată.</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="details" className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Detalii pacienți cu urmărire la 30 de zile</CardTitle>
                                    <p className="text-sm text-muted-foreground">
                                        Pacienții cu date de urmărire disponibile în perioada specificată
                                    </p>
                                </CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="w-full border-collapse">
                                            <thead>
                                                <tr className="border-b">
                                                    <th className="text-left p-2">Nume</th>
                                                    <th className="text-left p-2">CNP</th>
                                                    <th className="text-center p-2">Data nașterii</th>
                                                    <th className="text-center p-2">Vârstă gestațională</th>
                                                    <th className="text-center p-2">Greutate naștere</th>
                                                    <th className="text-center p-2">Status 30 zile</th>
                                                    <th className="text-center p-2">Reoperație</th>
                                                    <th className="text-center p-2">Readmisie</th>
                                                    <th className="text-center p-2">Complicații</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.detailed_patients && data.detailed_patients.length > 0 ? (
                                                    data.detailed_patients
                                                        .slice(0, 50)
                                                        .map((patient, index) => (
                                                            <tr key={index} className="border-b hover:bg-gray-50">
                                                                <td className="p-2 font-medium">
                                                                    {patient.name || '-'}
                                                                </td>
                                                                <td className="p-2 text-sm text-gray-600">{patient.cnp || '-'}</td>
                                                                <td className="text-center p-2 text-sm">{patient.birth_date || '-'}</td>
                                                                <td className="text-center p-2">{patient.gestational_age ?? '-'}</td>
                                                                <td className="text-center p-2">{patient.birth_weight ? `${patient.birth_weight}g` : '-'}</td>
                                                                <td className="text-center p-2">
                                                                    <Badge variant={patient.vital_status === 'Deceased' ? 'destructive' : 'default'}>
                                                                        {patient.vital_status === 'Deceased' ? 'Decedat' : 'Viu'}
                                                                    </Badge>
                                                                </td>
                                                                <td className="text-center p-2">
                                                                    <span className="text-gray-400">-</span>
                                                                </td>
                                                                <td className="text-center p-2">
                                                                    <span className="text-gray-400">-</span>
                                                                </td>
                                                                <td className="text-center p-2">
                                                                    <span className="text-gray-400">-</span>
                                                                </td>
                                                            </tr>
                                                        ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={9} className="text-center py-8 text-muted-foreground">
                                                            Nu există pacienți cu date de urmărire pentru perioada selectată.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {data.detailed_patients.length > 50 && (
                                        <div className="mt-4 text-sm text-muted-foreground text-center">
                                            Afișați primii 50 de pacienți din {data.detailed_patients.length} total
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </>
            )}
        </div>
    );
};

export { OutcomesAnalysisReport };
